import React, { useContext, useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Container, Col, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import axios from "axios";
import PageLayout from "../../../layouts/PageLayout";
import { TranslatorContext } from "../../../context/Translator";

const roleCards = {
    admin: [
        { key: "all_products", label: "All Products", icon: "inventory_2", color: "primary", path: "/products" },
        { key: "all_orders", label: "All Orders", icon: "shopping_cart", color: "success", path: "/admin/web-orders" },
        { key: "all_issuers", label: "Issuers", icon: "badge", color: "info", path: "/issuers" },
        { key: "all_listers", label: "Listers", icon: "assignment_ind", color: "warning", path: "/listers" },
        { key: "all_checkers", label: "Checkers", icon: "fact_check", color: "danger", path: "/checkers" },
        { key: "all_market_placers", label: "Market Placers", icon: "storefront", color: "secondary", path: "/market-placer" }
    ],
    issuer: [
        { key: "fresh_products", label: "Fresh Products", icon: "add_box", color: "primary", path: "/issue-product-list?tab=new" },
        { key: "assign_products", label: "Assign Products", icon: "send", color: "warning", path: "/assign-products-to-lister" },
        { key: "issued_products", label: "Issued Products", icon: "check_circle", color: "success", path: "/issue-product-list?tab=issued" }
    ],
    lister: [
        { key: "new_products", label: "New Products", icon: "shopping_bag", color: "primary", path: "/lister-products?tab=new" },
        { key: "received_products", label: "Received Products", icon: "inventory_2", color: "info", path: "/lister-products?tab=recieved" },
        { key: "listed_products", label: "Listed Products", icon: "widgets", color: "success", path: "/lister-products?tab=listed" },
        { key: "approved_products", label: "Approved Products", icon: "verified_user", color: "warning", path: "/lister-products?tab=approved" }
    ],
    checker: [
        { key: "new_products", label: "New Products", icon: "shopping_bag", color: "primary", path: "/checker-products?tab=new" },
        { key: "received_products", label: "Received Products", icon: "move_to_inbox", color: "info", path: "/checker-products?tab=checked" },
        { key: "checked_products", label: "Checked Products", icon: "verified_user", color: "success", path: "/checker-products?tab=overall_checked" },
        { key: "rejected_products", label: "Rejected Products", icon: "gpp_bad", color: "danger", path: "/checker-products?tab=rejected" }
    ],
    mplacer: [
        { key: "new_products", label: "New Products", icon: "shopping_bag", color: "primary", path: "/mplacer-products?tab=new" },
        { key: "listed_products", label: "Marketplaced", icon: "storefront", color: "success", path: "/mplacer-products?tab=marketplaced" },
        { key: "sold_products", label: "Sold Products", icon: "sell", color: "warning", path: "/mplacer-products?tab=sold" }
    ]
};

const storeSellerCards = [
    { key: "all_products", label: "All Products", icon: "inventory_2", color: "primary", path: "/seller-products" },
    { key: "approved_products", label: "Approved Products", icon: "verified", color: "success", path: "/seller-products" },
    { key: "pending_products", label: "Pending Products", icon: "pending_actions", color: "warning", path: "/seller-products" },
    { key: "sold_products", label: "Sold Products", icon: "sell", color: "info", path: "/seller-products" },
    { key: "unsold_products", label: "Unsold Products", icon: "inventory", color: "secondary", path: "/seller-products" },
    { key: "orders", label: "Orders", icon: "receipt_long", color: "dark", path: "/seller-store-orders" },
    { key: "unshipped_orders", label: "Unshipped Orders", icon: "local_shipping", color: "danger", path: "/seller-store-orders" },
    { key: "delivered_orders", label: "Delivered Orders", icon: "task_alt", color: "success", path: "/seller-store-orders" },
];

const asList = (value) => Array.isArray(value) ? value : [];

function StoreSellerDashboardSection({ data, loading, error }) {
    const money = (value) => `$${Number(value || 0).toFixed(2)}`;
    const soldProducts = asList(data?.sold_products?.data);
    const deliveredOrders = asList(data?.delivered_orders?.data);

    return (
        <div className="mb-5">
            <div className="d-flex align-items-center mb-3">
                <h4 className="fw-bold text-dark mb-0 border-start border-4 border-primary ps-2">
                    Store Seller Dashboard
                </h4>
            </div>
            {error && <div className="alert alert-danger" role="alert">{error}</div>}
            {loading ? (
                <div className="mc-card text-center py-4 shadow-sm bg-white rounded-3">
                    <div className="spinner-border text-primary mb-2" role="status" />
                    <p className="text-muted mb-0">Loading store dashboard...</p>
                </div>
            ) : !error && (
                <>
                    <Row className="g-4">
                        {storeSellerCards.map((card) => {
                            const count = card.key === "sold_products" || card.key === "delivered_orders"
                                ? data?.[card.key]?.total
                                : data?.[card.key];
                            return (
                                <Col key={card.key} xs={12} sm={6} lg={4} xl={3} className="d-flex">
                                    <Link to={card.path} className="text-decoration-none w-100">
                                        <div className="stat-card w-100 h-100 p-4 bg-white rounded-3 shadow-sm border d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center gap-3">
                                                <div className={`stat-icon bg-${card.color} text-white p-3 rounded-2 d-flex align-items-center justify-content-center shadow-sm`} style={{ width: "55px", height: "55px" }}>
                                                    <i className="material-icons fs-4">{card.icon}</i>
                                                </div>
                                                <div className="stat-content">
                                                    <h3 className="fw-bold mb-1 text-dark fs-3">{Number(count || 0)}</h3>
                                                    <p className="text-muted mb-0 fw-medium">{card.label}</p>
                                                </div>
                                            </div>
                                            <i className="material-icons text-muted opacity-50">arrow_forward_ios</i>
                                        </div>
                                    </Link>
                                </Col>
                            );
                        })}
                    </Row>

                    {data?.account && (
                        <div className="mc-card p-3 bg-white rounded-3 shadow-sm border mt-4 d-flex flex-wrap gap-4 align-items-center">
                            <div><span className="text-muted small d-block">Store</span><strong>{data.account.store_name || "-"}</strong></div>
                            <div><span className="text-muted small d-block">Storage</span><strong>{data.account.storage_label || data.account.storage_preference}</strong></div>
                            <div><span className="text-muted small d-block">{data.account.storage_preference === "admin_store" ? "Commission" : "Platform fee"}</span><strong>{Number(data.account.fee_percent || 0)}%</strong></div>
                            {data.account.product_limit > 0 && (
                                <div><span className="text-muted small d-block">Listing slots left</span><strong>{data.account.remaining_product_slots ?? "-"} / {data.account.product_limit}</strong></div>
                            )}
                            <Link to="/seller-account" className="ms-auto small">Account &amp; verification</Link>
                        </div>
                    )}

                    {data?.payouts && (
                        <Row className="g-4 mt-1">
                            {[
                                ["Net earned", data.payouts.net_earned_usd, "text-dark"],
                                ["On hold", Number(data.payouts.on_hold_usd || 0) + Number(data.payouts.complaint_hold_usd || 0), "text-warning"],
                                ["Ready for payout", Number(data.payouts.eligible_usd || 0) + Number(data.payouts.approved_usd || 0), "text-primary"],
                                ["Paid to you", data.payouts.paid_usd, "text-success"],
                            ].map(([label, value, cls]) => (
                                <Col key={label} xs={12} sm={6} xl={3}>
                                    <Link to="/seller-payouts" className="text-decoration-none">
                                        <div className="mc-card p-4 bg-white rounded-3 shadow-sm border h-100">
                                            <p className="text-muted mb-1">{label}</p>
                                            <h4 className={`fw-bold mb-0 ${cls}`}>{money(value)}</h4>
                                        </div>
                                    </Link>
                                </Col>
                            ))}
                        </Row>
                    )}

                    <Row className="g-4 mt-1">
                        <Col xs={12} md={4}>
                            <div className="mc-card p-4 bg-white rounded-3 shadow-sm border h-100">
                                <p className="text-muted mb-1">Total Reviews</p>
                                <h4 className="fw-bold mb-3">{Number(data?.reviews?.total || 0)}</h4>
                                <div className="d-flex justify-content-between">
                                    <span>Approved</span><strong>{Number(data?.reviews?.approved || 0)}</strong>
                                </div>
                                <div className="d-flex justify-content-between mt-2">
                                    <span>Pending</span><strong>{Number(data?.reviews?.pending || 0)}</strong>
                                </div>
                            </div>
                        </Col>
                        <Col xs={12} md={4}>
                            <div className="mc-card p-4 bg-white rounded-3 shadow-sm border h-100">
                                <p className="text-muted mb-1">Platform Fee{data?.account ? ` (${Number(data.account.fee_percent || 0)}%)` : ""}</p>
                                <h4 className="fw-bold mb-0">{money(data?.platform_fee)}</h4>
                            </div>
                        </Col>
                        <Col xs={12} md={4}>
                            <div className="mc-card p-4 bg-white rounded-3 shadow-sm border h-100">
                                <p className="text-muted mb-1">Earned</p>
                                <h4 className="fw-bold mb-0">{money(data?.earned_dollar)}</h4>
                            </div>
                        </Col>
                    </Row>

                    <Row className="g-4 mt-1">
                        <Col xs={12} xl={6}>
                            <div className="mc-card p-4 bg-white rounded-3 shadow-sm border h-100">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="fw-bold mb-0">Sold Products</h5>
                                    <Link to="/seller-products">View products</Link>
                                </div>
                                {soldProducts.length === 0 ? (
                                    <p className="text-muted mb-0">No sold products found.</p>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle mb-0">
                                            <thead><tr><th>Product</th><th>Order</th><th>Buyer</th><th>Price</th></tr></thead>
                                            <tbody>
                                                {soldProducts.map((product, index) => (
                                                    <tr key={product.id || index}>
                                                        <td>{product.title || product.product_code || "-"}</td>
                                                        <td>{product.order_number || "-"}</td>
                                                        <td>{product.buyer_name || product.buyer_email || "-"}</td>
                                                        <td>{money(product.sell_price_dollar)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </Col>
                        <Col xs={12} xl={6}>
                            <div className="mc-card p-4 bg-white rounded-3 shadow-sm border h-100">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="fw-bold mb-0">Delivered Orders</h5>
                                    <Link to="/seller-store-orders">View orders</Link>
                                </div>
                                {deliveredOrders.length === 0 ? (
                                    <p className="text-muted mb-0">No delivered orders found.</p>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle mb-0">
                                            <thead><tr><th>Order</th><th>Product</th><th>Buyer</th><th>Price</th><th>Tracking</th></tr></thead>
                                            <tbody>
                                                {deliveredOrders.map((order, index) => (
                                                    <tr key={order.id || index}>
                                                        <td>{order.order_number || "-"}</td>
                                                        <td>{order.product?.title || "-"}</td>
                                                        <td>{order.buyer_name || order.buyer_email || "-"}</td>
                                                        <td>{money(order.unit_price)}</td>
                                                        <td>{order.tracking_number || "-"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                </>
            )}
        </div>
    );
}

export default function DashboardPage() {
    const { t } = useContext(TranslatorContext);
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState({});
    const [apiRoles, setApiRoles] = useState([]);
    const [storeDashboardData, setStoreDashboardData] = useState({});
    const [storeDashboardLoading, setStoreDashboardLoading] = useState(true);
    const [storeDashboardError, setStoreDashboardError] = useState("");

    const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
    const fallbackRoles = (currentUser?.role || "")
        .split(",")
        .map((role) => role.trim().toLowerCase())
        .filter(Boolean);

    const isStoreSeller = fallbackRoles.includes("store");
    const validRoles = ["admin", "issuer", "lister", "checker", "mplacer", "store"];

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/dashboard`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
                });

                if (response.data.success) {
                    setDashboardData(response.data.data || {});
                    if (response.data.roles && Array.isArray(response.data.roles)) {
                        setApiRoles(response.data.roles.map((r) => r.toLowerCase().trim()));
                    }
                }
            } catch (error) {
                console.error("Error fetching dashboard:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    useEffect(() => {
        if (!isStoreSeller) return undefined;

        const fetchStoreDashboard = async () => {
            setStoreDashboardLoading(true);
            setStoreDashboardError("");
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/store-seller/dashboard`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
                });
                setStoreDashboardData(response.data?.data || response.data || {});
            } catch (error) {
                console.error("Error fetching store seller dashboard:", error);
                setStoreDashboardError(error.response?.data?.message || "Could not load store seller dashboard.");
            } finally {
                setStoreDashboardLoading(false);
            }
        };

        fetchStoreDashboard();
    }, [isStoreSeller]);

    const userActiveRoles = (apiRoles.length > 0 ? apiRoles : fallbackRoles).filter((r) =>
        validRoles.includes(r)
    );

    const getCardValue = (roleName, cardKey) => {
        if (dashboardData[roleName] && dashboardData[roleName][cardKey] !== undefined) {
            return Number(dashboardData[roleName][cardKey]);
        }
        return Number(dashboardData[cardKey] || 0);
    };

    const allCards = userActiveRoles.flatMap((r) => roleCards[r] || []);
    const chartData = allCards.map((card) => {
        
        const ownerRole = userActiveRoles.find((r) =>
            roleCards[r]?.some((c) => c.key === card.key)
        );
        return {
            name: card.label,
            value: ownerRole ? getCardValue(ownerRole, card.key) : 0
        };
    });

    if (loading) {
        return (
            <PageLayout>
                <Container fluid className="px-3 py-4">
                    <div className="mc-card text-center py-5 shadow-sm bg-white rounded-3">
                        <div className="spinner-border text-primary mb-3" role="status" />
                        <p className="text-muted mb-0">Loading Dashboard...</p>
                    </div>
                </Container>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Container fluid className="px-3 px-md-4 py-3">
                {/* Breadcrumb Header */}
                <Row className="mb-4">
                    <Col xs={12}>
                        <div className="mc-card p-4 shadow-sm border-0 rounded-3 bg-white">
                            <div className="mc-breadcrumb d-flex flex-column flex-md-row justify-content-between align-items-md-center">
                                <h3 className="mc-breadcrumb-title mb-2 mb-md-0 fw-bold text-dark">
                                    {t("dashboard") || "Dashboard"}
                                </h3>
                                <ul className="mc-breadcrumb-list list-inline mb-0">
                                    <li className="mc-breadcrumb-item list-inline-item">
                                        <Link to="/dashboard" className="mc-breadcrumb-link text-decoration-none text-primary">
                                            {t("home") || "Home"}
                                        </Link>
                                    </li>
                                    <li className="mc-breadcrumb-item list-inline-item text-muted">/ Dashboard</li>
                                </ul>
                            </div>
                        </div>
                    </Col>
                </Row>

                {isStoreSeller && (
                    <StoreSellerDashboardSection
                        data={storeDashboardData}
                        loading={storeDashboardLoading}
                        error={storeDashboardError}
                    />
                )}

                {/* Dynamic Role Sections with Titles */}
                {userActiveRoles.map((r) => {
                    const roleSpecificCards = roleCards[r] || [];
                    if (roleSpecificCards.length === 0) return null;

                    const roleTitle = r === "mplacer" ? "Market Placer" : r.charAt(0).toUpperCase() + r.slice(1);

                    return (
                        <div key={r} className="mb-5">
                            <div className="d-flex align-items-center mb-3">
                                <h4 className="fw-bold text-dark mb-0 border-start border-4 border-primary ps-2">
                                    {roleTitle} Dashboard
                                </h4>
                            </div>
                            <Row className="g-4">
                                {roleSpecificCards.map((card) => {
                                    const val = getCardValue(r, card.key);
                                    return (
                                        <Col key={card.key} xs={12} sm={6} lg={4} className="d-flex">
                                            <Link to={card.path} className="text-decoration-none w-100">
                                                <div className="stat-card w-100 h-100 p-4 bg-white rounded-3 shadow-sm border d-flex align-items-center justify-content-between">
                                                    <div className="d-flex align-items-center gap-3">
                                                        <div className={`stat-icon bg-${card.color} text-white p-3 rounded-2 d-flex align-items-center justify-content-center shadow-sm`} style={{ width: "55px", height: "55px" }}>
                                                            <i className="material-icons fs-4">{card.icon}</i>
                                                        </div>
                                                        <div className="stat-content">
                                                            <h3 className="fw-bold mb-1 text-dark fs-3">{val}</h3>
                                                            <p className="text-muted mb-0 fw-medium">{card.label}</p>
                                                        </div>
                                                    </div>
                                                    <i className="material-icons text-muted opacity-50">arrow_forward_ios</i>
                                                </div>
                                            </Link>
                                        </Col>
                                    );
                                })}
                            </Row>
                        </div>
                    );
                })}

                {/* Chart Section */}
                {chartData.length > 0 && <Row>
                    <Col xs={12}>
                        <div className="mc-card p-4 bg-white rounded-3 shadow-sm border">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h4 className="card-title fw-bold text-dark mb-0">
                                    Overview Analytics
                                </h4>
                            </div>
                            <div style={{ width: "100%", height: 350 }}>
                                <ResponsiveContainer>
                                    <BarChart data={chartData} margin={{ top: 10, right: 20, left: -10, bottom: 30 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                        <XAxis
                                            dataKey="name"
                                            tick={{ fill: '#6c757d', fontSize: 12 }}
                                            interval={0}
                                            angle={-15}
                                            textAnchor="end"
                                        />
                                        <YAxis allowDecimals={false} tick={{ fill: '#6c757d', fontSize: 12 }} />
                                        <Tooltip cursor={{ fill: 'rgba(35, 81, 175, 0.05)' }} />
                                        <Bar dataKey="value" name="Count" fill="#2351af" radius={[6, 6, 0, 0]} barSize={40} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </Col>
                </Row>}
            </Container>
        </PageLayout>
    );
}